import * as React from "react";
import { useIcon } from "@salt-ds/core";
import { StatusAdornment } from "../display/StatusAdornment";

import { Button } from "../controls/Button";
import { useFormFieldProps } from "../salt/form-field-context";
import { useControlled } from "./useControlled";
import { useLongPressPointerAction } from "./useLongPressPointerAction";
import "./number-input.css";

/**
 * NumberInput, replacing `@salt-ds/core`'s behind `currency-input.tsx` and
 * `percentage-input.tsx`.
 *
 * ── IT KEEPS TWO REPRESENTATIONS, AND CONFLATING THEM IS THE CLASSIC BUG ──────────────
 *
 * `value` is the string the user is typing; `floatValue` is the number it means. While the
 * field is being EDITED the raw string is shown untouched, and only on blur is it parsed,
 * clamped and formatted back.
 *
 * That is what lets someone type "1." on the way to "1.5". Format on every keystroke and the
 * decimal point is eaten the instant it is typed, so the field cannot be used at all — the
 * single most common defect in a hand-written numeric input.
 *
 * ── decimalScale IS INFERRED FROM THE DATA, NOT ASSUMED ───────────────────────────────
 *
 * It defaults to the greater precision of the initial value and the step. A currency field
 * stepping by 0.01 gets two decimals without being told, and a whole-number field is not
 * silently turned into "5.00".
 *
 * ── THE ROLE CHANGES WHEN READ-ONLY ───────────────────────────────────────────────────
 *
 * `spinbutton` normally, `textbox` when read-only — because a spinbutton promises the value can
 * be stepped, and announcing that for a field nobody can change is a false promise. The min,
 * max and now values are dropped with it, for the same reason.
 */
export type NumberInputVariant = "primary" | "secondary";
export type NumberInputValidationStatus = "error" | "warning" | "success";
type DataAttributes = Record<`data-${string}`, unknown>;

export function isOutOfRange(value: string | number, min: number, max: number): boolean {
  if (typeof value === "string" && !value.length) return true;
  const floatValue = typeof value === "string" ? Number.parseFloat(value) : value;
  return Number.isNaN(floatValue) || floatValue > max || floatValue < min;
}

/**
 * How many decimals a number is written with — including exponent form, where `1e-4` means four
 * decimal places and a naive `split(".")` finds none.
 */
function getNumberPrecision(num: number | string): number {
  const numStr = String(num);
  if (numStr.includes("e") || numStr.includes("E")) {
    const [base, exponent] = numStr.split(/[eE]/);
    const decimalPart = base.split(".")[1] || "";
    return Math.max(0, decimalPart.length - Number.parseInt(exponent, 10));
  }
  if (numStr.includes(".")) return numStr.split(".")[1].length;
  return 0;
}

// Accepts partial input on the way to a number: "", "-", ".", "1.", "+1.5" all pass.
const defaultPattern = (inputValue: string) => /^[+-]?(\d+(\.\d*)?|\.\d*)?$/.test(inputValue);

const defaultFormat = (value: string, decimalScale: number): string => {
  const sanitized = value.trim();
  if (!sanitized.length) return "";
  const floatValue = Number.parseFloat(sanitized);
  return String(Number.isNaN(floatValue) ? sanitized : floatValue.toFixed(decimalScale));
};

const defaultParse = (value: string, decimalScale: number): number | null => {
  const sanitized = value.trim();
  if (!sanitized.length) return null;
  // A lone sign or point is a number in progress, not a number: treat it as 0 so stepping from
  // it works, without discarding what the user has typed.
  if (sanitized === "." || sanitized === "+" || sanitized === "-") return 0;
  return Number.parseFloat(Number.parseFloat(value).toFixed(decimalScale));
};

export interface NumberInputProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "defaultValue" | "onChange"> {
  bordered?: boolean;
  clamp?: boolean;
  step?: number;
  stepMultiplier?: number;
  value?: number | string;
  defaultValue?: number | string;
  decimalScale?: number;
  disabled?: boolean;
  emptyReadOnlyMarker?: string;
  endAdornment?: React.ReactNode;
  hideButtons?: boolean;
  inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>> & DataAttributes;
  inputRef?: React.Ref<HTMLInputElement>;
  max?: number;
  min?: number;
  placeholder?: string;
  readOnly?: boolean;
  startAdornment?: React.ReactNode;
  textAlign?: "left" | "center" | "right";
  validationStatus?: NumberInputValidationStatus;
  variant?: NumberInputVariant;
  "aria-valuetext"?: string;
  format?: (value: string) => string;
  parse?: (value: string) => number | null;
  pattern?: (value: string) => boolean;
  increment?: (value: string, step: number, multiplier: number) => string;
  decrement?: (value: string, step: number, multiplier: number) => string;
  onChange?: (event: React.SyntheticEvent | null, value: string) => void;
  onNumberChange?: (event: React.SyntheticEvent | null, value: number | null) => void;
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

function join(...values: (string | undefined)[]): string | undefined {
  return values.filter(Boolean).join(" ") || undefined;
}

export const NumberInput = React.forwardRef<HTMLDivElement, NumberInputProps>(
  function NumberInput(props, ref) {
    const {
      "aria-valuetext": ariaValueTextProp,
      bordered,
      className,
      clamp,
      /*
       * These six are pulled out ONLY so they never reach the DOM spread below. They are read
       * back off `props` a few lines down, because each needs `decimalScale` to build its
       * default and that is computed after this destructure.
       *
       * Leaving them in `restProps` is not harmless: React is handed a function as the value of
       * a `format` attribute on a div, refuses it, and warns — "Invalid values for props
       * `format`, `parse`, `pattern` on <div> tag" — once per instance. MEASURED on
       * /register-spend, which renders several currency inputs. Nothing lands in the DOM,
       * because React drops them, so the only symptom is console noise that trains everyone to
       * ignore console noise.
       */
      decimalScale: _decimalScale,
      decrement: _decrement,
      format: _format,
      increment: _increment,
      parse: _parse,
      pattern: _pattern,
      defaultValue,
      disabled,
      emptyReadOnlyMarker = "—",
      endAdornment,
      hideButtons,
      id,
      inputProps = {},
      inputRef: inputRefProp,
      max = Number.MAX_SAFE_INTEGER,
      min = Number.MIN_SAFE_INTEGER,
      onBlur,
      onChange,
      onMouseUp,
      onNumberChange,
      placeholder,
      readOnly,
      startAdornment,
      step = 1,
      stepMultiplier = 2,
      textAlign = "left",
      validationStatus: validationStatusProp,
      value: valueProp,
      variant = "primary",
      ...restProps
    } = props;

    // Inferred from the data rather than assumed — see the note at the top.
    const decimalScale =
      props.decimalScale ??
      Math.max(getNumberPrecision(valueProp ?? defaultValue ?? 0), getNumberPrecision(step));

    const format = props.format ?? ((v: string) => defaultFormat(v, decimalScale));
    const parse = props.parse ?? ((v: string) => defaultParse(v, decimalScale));
    const pattern = props.pattern ?? defaultPattern;
    const increment =
      props.increment ??
      ((v: string, s: number, m: number) => String((parse(v) ?? 0) + s * m));
    const decrement =
      props.decrement ??
      ((v: string, s: number, m: number) => String((parse(v) ?? 0) - s * m));

    const {
      a11yProps: {
        "aria-describedby": formFieldDescribedBy,
        "aria-labelledby": formFieldLabelledBy,
      } = {},
      disabled: formFieldDisabled,
      readOnly: formFieldReadOnly,
      necessity: formFieldRequired,
      validationStatus: formFieldValidationStatus,
    } = useFormFieldProps();

    const isDisabled = disabled || formFieldDisabled;
    const isReadOnly = readOnly || formFieldReadOnly;
    const validationStatus = formFieldValidationStatus ?? validationStatusProp;
    const generatedId = React.useId();
    const validationStatusId = id ?? generatedId;

    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof inputRefProp === "function") inputRefProp(node);
        else if (inputRefProp)
          (inputRefProp as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [inputRefProp],
    );

    const {
      "aria-describedby": inputDescribedBy,
      "aria-labelledby": inputLabelledBy,
      className: inputClassName,
      onBlur: inputOnBlur,
      onFocus: inputOnFocus,
      onKeyDown: inputOnKeyDown,
      required: inputRequired,
      ...restInputProps
    } = inputProps;

    const isRequired = formFieldRequired
      ? ["required", "asterisk"].includes(formFieldRequired)
      : inputRequired;

    const [isFocused, setIsFocused] = React.useState(false);
    // `isEditing` is what protects a half-typed number from being formatted out from under the
    // user. See the note at the top of this file.
    const [isEditing, setIsEditing] = React.useState(false);
    const { DecreaseIcon, IncreaseIcon } = useIcon();

    const [value, setValue] = useControlled<string>({
      controlled: valueProp !== undefined ? String(valueProp) : undefined,
      default: String(defaultValue ?? ""),
    });

    const lastCommitValue = React.useRef(value);

    const commit = (
      event: React.SyntheticEvent | null,
      newNumber: number | null,
      newInputValue: string,
    ) => {
      let safeNumber = newNumber;
      if (safeNumber !== null && !Number.isNaN(safeNumber)) {
        // Always bounded by the safe-integer range; bounded by min/max only when `clamp` is on,
        // so a field can legitimately hold an out-of-range value and SHOW it as invalid rather
        // than silently rewriting what the user typed.
        safeNumber = Math.max(Number.MIN_SAFE_INTEGER, Math.min(Number.MAX_SAFE_INTEGER, safeNumber));
        if (clamp) safeNumber = Math.max(min, Math.min(max, safeNumber));
      } else {
        safeNumber = null;
      }
      const commitValue = safeNumber !== null ? format(String(safeNumber)) : newInputValue;
      if (commitValue !== value) {
        setValue(commitValue);
        onChange?.(event, commitValue);
      }
      // Tracked separately: the STRING can change without the NUMBER changing ("1" -> "1.00"),
      // and a consumer listening for numbers should not see an edit that means nothing to them.
      if (lastCommitValue.current !== commitValue) {
        onNumberChange?.(event, safeNumber);
        lastCommitValue.current = commitValue;
      }
    };

    let floatValue = parse(value) ?? 0;
    floatValue = Math.max(Number.MIN_SAFE_INTEGER, Math.min(Number.MAX_SAFE_INTEGER, floatValue));
    if (clamp) floatValue = Math.max(min, Math.min(max, floatValue));

    const decrementValue = (event?: React.SyntheticEvent | null, block?: boolean) => {
      const validValue = parse(value) ?? 0;
      if (Number.isNaN(validValue)) return;
      const next = decrement(String(validValue), step, block ? stepMultiplier : 1);
      commit(event ?? null, parse(next), next);
    };
    const incrementValue = (event?: React.SyntheticEvent | null, block?: boolean) => {
      const validValue = parse(value) ?? 0;
      if (Number.isNaN(validValue)) return;
      const next = increment(String(validValue), step, block ? stepMultiplier : 1);
      commit(event ?? null, parse(next), next);
    };

    const activateDecrement = useLongPressPointerAction(decrementValue, floatValue <= min);
    const activateIncrement = useLongPressPointerAction(incrementValue, floatValue >= max);

    React.useEffect(() => {
      if (isFocused) innerRef.current?.focus();
    }, [isFocused]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        // `preventDefault` on the arrows stops the caret jumping to either end of the text while
        // the value steps — the browser's own behaviour for an input, and wrong here.
        case "ArrowUp":
          event.preventDefault();
          incrementValue(event, event.shiftKey);
          break;
        case "ArrowDown":
          event.preventDefault();
          decrementValue(event, event.shiftKey);
          break;
        case "Home":
          event.preventDefault();
          commit(event, min, String(min));
          break;
        case "End":
          event.preventDefault();
          commit(event, max, String(max));
          break;
        case "PageUp":
          event.preventDefault();
          incrementValue(event, true);
          break;
        case "PageDown":
          event.preventDefault();
          decrementValue(event, true);
          break;
      }
      inputOnKeyDown?.(event);
    };

    let renderedValue: string;
    if (isEditing) {
      renderedValue = value;
    } else if (!value?.length) {
      renderedValue = "";
    } else {
      renderedValue = format(Number.isNaN(floatValue) ? value : String(floatValue));
    }

    const disableDecrement = disabled || floatValue - step < min;
    const disableIncrement = disabled || floatValue + step > max;

    const classes = ["saltNumberInput", `saltNumberInput-${variant}`];
    if (isFocused) classes.push("saltNumberInput-focused");
    if (isDisabled) classes.push("saltNumberInput-disabled");
    if (isReadOnly) classes.push("saltNumberInput-readOnly");
    if (hideButtons) classes.push("saltNumberInput-hiddenButtons");
    if (validationStatus) classes.push(`saltNumberInput-${validationStatus}`);
    if (bordered) classes.push("saltNumberInput-bordered");
    if (className) classes.push(className);

    const stepperPointerDown = (
      event: React.PointerEvent,
      atLimit: boolean,
      activate: (e: React.PointerEvent) => void,
    ) => {
      event.preventDefault();
      if (!atLimit) {
        setIsEditing(false);
        activate(event);
      } else {
        // At the limit the press selects the text instead — the button did nothing, so give the
        // click somewhere useful to land rather than swallowing it.
        innerRef.current?.select();
      }
    };

    return (
      <div
        className={classes.join(" ")}
        onBlur={(event) => {
          setIsFocused(false);
          onBlur?.(event);
        }}
        onMouseUp={(event) => {
          setIsFocused(true);
          onMouseUp?.(event);
        }}
        {...restProps}
        ref={ref}
      >
        {startAdornment ? (
          <div className="saltNumberInput-startAdornmentContainer">{startAdornment}</div>
        ) : null}
        <input
          aria-describedby={join(formFieldDescribedBy, inputDescribedBy)}
          // Out-of-range is reported as invalid even without `clamp`, so a field that ACCEPTS a
          // bad value still says so rather than looking fine.
          aria-invalid={
            !isReadOnly && renderedValue.length
              ? isOutOfRange(floatValue, min, max) || validationStatus === "error"
              : undefined
          }
          aria-labelledby={join(formFieldLabelledBy, inputLabelledBy)}
          aria-readonly={isReadOnly ? "true" : undefined}
          aria-valuemax={!isReadOnly && renderedValue.length ? max : undefined}
          aria-valuemin={!isReadOnly && renderedValue.length ? min : undefined}
          aria-valuenow={!isReadOnly ? floatValue : undefined}
          aria-valuetext={
            !isReadOnly
              ? renderedValue.length
                ? (ariaValueTextProp ?? renderedValue)
                : "Empty"
              : undefined
          }
          className={[
            "saltNumberInput-input",
            `saltNumberInput-inputTextAlign${capitalize(textAlign)}`,
            inputClassName,
          ]
            .filter(Boolean)
            .join(" ")}
          disabled={isDisabled}
          onBlur={(event) => {
            setIsEditing(false);
            inputOnBlur?.(event);
            commit(event, parse(value), value);
          }}
          onChange={(event) => {
            const inputValue = event.currentTarget.value;
            if (!inputValue.length) {
              setValue("");
              onChange?.(event, "");
              return;
            }
            // Rejected input is simply not accepted — the field keeps its previous text rather
            // than showing something that is not a number in progress.
            if (pattern(inputValue)) {
              setIsEditing(true);
              onChange?.(event, inputValue);
              setValue(inputValue);
            } else {
              event.preventDefault();
            }
          }}
          onFocus={(event) => {
            setIsEditing(false);
            inputOnFocus?.(event);
          }}
          onKeyDown={isReadOnly ? undefined : handleInputKeyDown}
          placeholder={placeholder}
          readOnly={isReadOnly}
          ref={setInputRef}
          required={isRequired}
          // `textbox` when read-only: `spinbutton` promises the value can be stepped, which is a
          // false promise for a field nobody can change.
          role={isReadOnly ? "textbox" : "spinbutton"}
          tabIndex={isDisabled ? -1 : 0}
          value={isReadOnly && renderedValue.length === 0 ? emptyReadOnlyMarker : renderedValue}
          {...restInputProps}
        />
        <div className="saltNumberInput-activationIndicator" />
        {!isDisabled && validationStatus ? (
          <StatusAdornment id={validationStatusId} status={validationStatus} />
        ) : null}
        {endAdornment ? (
          <div className="saltNumberInput-endAdornmentContainer">{endAdornment}</div>
        ) : null}
        {!isReadOnly ? (
          <div className="saltNumberInput-buttonContainer">
            {/*
              The steppers are `aria-hidden` and `tabIndex={-1}` on purpose: the INPUT is already
              a spinbutton, and arrow keys step it. Exposing them adds two tab stops and two
              announcements for a control the user already has.
            */}
            <Button
              appearance="transparent"
              aria-hidden
              className="saltNumberInput-increment"
              disabled={disableIncrement}
              onPointerDown={(event) =>
                stepperPointerDown(event, disableIncrement, activateIncrement)
              }
              tabIndex={-1}
            >
              <IncreaseIcon aria-hidden />
            </Button>
            <Button
              appearance="transparent"
              aria-hidden
              className="saltNumberInput-decrement"
              disabled={disableDecrement}
              onPointerDown={(event) =>
                stepperPointerDown(event, disableDecrement, activateDecrement)
              }
              tabIndex={-1}
            >
              <DecreaseIcon aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
    );
  },
);
